import { type ReactNode } from 'react'

const Code = ({ children }: { children: ReactNode }) => (
  <code className="rounded bg-base-300 px-1 py-0.5 font-mono text-xs">
    {children}
  </code>
)

const Example = ({ children }: { children: string }) => (
  <pre className="rounded-lg bg-base-300 p-3 font-mono text-xs whitespace-pre-wrap">
    {children}
  </pre>
)

const Section = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => (
  <section className="space-y-2">
    <h3 className="font-semibold">{title}</h3>
    {children}
  </section>
)

const operators: { operator: string; description: ReactNode }[] = [
  {
    operator: '==',
    description: (
      <>
        Equal. Compares as the type you write: <Code>"123"</Code> matches the
        string, <Code>123</Code> matches the number (or a numeric string),{' '}
        <Code>true</Code>/<Code>false</Code> match booleans, and{' '}
        <Code>null</Code> matches an explicit JSON null.
      </>
    ),
  },
  {
    operator: '!=',
    description: (
      <>
        Not equal. The path must exist: a missing field never matches. Through
        an array it means <em>some</em> element differs. Use{' '}
        <Code>not (field == value)</Code> for "no element equals", which also
        includes traces without the field.
      </>
    ),
  },
  {
    operator: '> >= < <=',
    description: (
      <>
        Ordering. Numbers compare numerically (numeric strings like{' '}
        <Code>"42.5"</Code> are converted); strings compare alphabetically,
        which works for ISO dates.
      </>
    ),
  },
  {
    operator: 'contains',
    description: (
      <>
        On an <strong>array</strong>, matches when an element equals the value
        exactly (<Code>["EXLG13", "LGPU"]</Code> contains <Code>"EXLG13"</Code>,
        not <Code>"EXLG"</Code>). On a <strong>string</strong>, matches a
        case-sensitive substring. Numbers are compared as text, so{' '}
        <Code>contains 5</Code> finds <Code>[5, 6]</Code>. For arrays of
        objects, compare a field instead:{' '}
        <Code>commodities.class == "125"</Code>.
      </>
    ),
  },
  {
    operator: 'exists(field)',
    description: (
      <>
        The field is present (even if its value is <Code>null</Code>).{' '}
        <Code>exists(error)</Code> means the transaction has an error.
      </>
    ),
  },
]

export const TraceFilterHelp = () => (
  <div className="space-y-6 text-sm -mt-6">
    <Section title="Overview">
      <p>
        A filter is one or more conditions of the form{' '}
        <Code>field operator value</Code>, combined with <Code>and</Code>,{' '}
        <Code>or</Code> and <Code>not</Code>. It runs together with the SCAC,
        layer, status and time filters above, and needs a SCAC or layer to be
        selected.
      </p>
      <Example>{`request.body.accessorials contains "EXLG13"
and request.body.account == "6033277"`}</Example>
    </Section>

    <Section title="Fields">
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <Code>request</Code> and <Code>response</Code> are the JSON bodies
          shown in the trace details. Walk into them with dots, array indexes
          and quoted keys: <Code>response.rates[0].total</Code>,{' '}
          <Code>request.headers["content-type"]</Code>.
        </li>
        <li>
          Paths step into arrays automatically and match if{' '}
          <strong>any element</strong> does:{' '}
          <Code>request.body.commodities.description == "Old silverware"</Code>{' '}
          matches when at least one commodity has that description. This works
          through nested arrays too (<Code>response.rates.charges.code</Code>).
          Use an index like <Code>commodities[0]</Code> to check one element.
        </li>
        <li>
          <Code>error</Code> is the transaction's error message, a plain string.
          It supports <Code>==</Code>, <Code>!=</Code>, <Code>contains</Code>{' '}
          and <Code>exists(error)</Code>.
        </li>
        <li>
          A bare <Code>request</Code> or <Code>response</Code> refers to the
          whole body, which is useful for XML/SOAP bodies stored as text:{' '}
          <Code>response contains "Fault"</Code>.
        </li>
      </ul>
    </Section>

    <Section title="Operators">
      <dl className="space-y-3">
        {operators.map(({ operator, description }) => (
          <div key={operator}>
            <dt>
              <Code>{operator}</Code>
            </dt>
            <dd className="mt-1 opacity-80">{description}</dd>
          </div>
        ))}
      </dl>
    </Section>

    <Section title="Values">
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Strings in double or single quotes; escape quotes with a backslash:{' '}
          <Code>"say \"hi\""</Code>.
        </li>
        <li>
          Numbers, including negatives and decimals: <Code>-5</Code>,{' '}
          <Code>12.75</Code>.
        </li>
        <li>
          <Code>true</Code>, <Code>false</Code> and <Code>null</Code>.
        </li>
        <li>The value always goes on the right of the operator.</li>
      </ul>
    </Section>

    <Section title="Combining conditions">
      <p>
        <Code>and</Code> binds tighter than <Code>or</Code>; use parentheses to
        group. Keywords are case-insensitive.
      </p>
      <p>
        Each condition matches when <strong>any</strong> transaction in the
        trace satisfies it, so one condition can match the internal request and
        another the carrier response. <Code>not</Code> means no transaction
        satisfies it.
      </p>
      <Example>{`exists(error)
and (request.body.accessorials contains "LGPU"
  or request.body.accessorials contains "LGDEL")`}</Example>
      <Example>{`not exists(response.body.rates)`}</Example>
    </Section>

    <Section title="Limitations">
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Very large bodies may be archived out of the searchable data and won't
          match.
        </li>
        <li>
          Long strings in non-rating responses are stored as{' '}
          <Code>"[large string]"</Code>.
        </li>
        <li>
          Body filters scan a lot of data; keep the time range as narrow as you
          can.
        </li>
      </ul>
    </Section>
  </div>
)
